import { z } from "zod";
import {
  companyStageSchema,
  contactStatusSchema,
  domainSchema,
  emailSchema,
  emailStatusSchema,
  enrichmentFactsSchema,
  importKindSchema,
  outcomeKindSchema,
  referenceClientSchema,
  markdownSchema,
  runFinishStatusSchema,
  scoreSchema,
  sourceTypeSchema,
  strategyKeySchema,
  strategyTypeSchema,
  suppressionKindSchema,
  warmTriggerSchema,
} from "./pipeline.js";

/**
 * The agent contract (docs/03 §4).
 *
 * Every mutating tool takes a `run_id`, so nothing enters the database without
 * a traceable author, and every input is parsed here rather than in the
 * handler — the MCP layer and the REST layer validate against the same
 * schemas, which is the only way the two entry points can't drift.
 *
 * Field names are snake_case because these cross the wire to an LLM, and the
 * tool descriptions the model reads use snake_case throughout.
 */

const runIdSchema = z.string().uuid();
const idSchema = z.string().uuid();

/** docs/04 S3: "cap 25/run". Also the batch size that keeps review queues sane. */
export const MAX_COMPANIES_PER_CALL = 25;
export const MAX_CONTACTS_PER_CALL = 10;

// --- Orchestration & context ------------------------------------------------

export const getContextInput = z.object({}).strict();

export const startRunInput = z
  .object({
    strategy_key: strategyKeySchema,
    /** Optional note shown on the Runs log, e.g. "focused on fintech". */
    note: z.string().max(500).optional(),
  })
  .strict();

export const logEventInput = z
  .object({
    run_id: runIdSchema,
    /** Free-form label: `searched`, `skipped`, `novel_approach`, `warning`. */
    kind: z.string().min(1).max(60),
    message: z.string().min(1).max(4000),
    entity_type: z.enum(["company", "contact", "sequence", "strategy"]).optional(),
    entity_id: idSchema.optional(),
  })
  .strict();

export const finishRunInput = z
  .object({
    run_id: runIdSchema,
    status: runFinishStatusSchema,
    summary_md: markdownSchema,
    /** Free-form tallies, e.g. `{"companies_created": 14, "duplicates": 3}`. */
    stats: z.record(z.number()).optional(),
  })
  .strict();

/**
 * The kinds of work an agent can ask for.
 *
 * This is how a scheduled agent self-directs (docs/03 §4): rather than being
 * told which companies to enrich, it asks what needs enriching. Each kind maps
 * to a query in the work-queue service.
 */
export const workQueueKindSchema = z.enum([
  "qualification",
  "contact_finding",
  "enrichment",
  "person_enrichment",
  "drafting",
  "sending",
  "imports",
]);
export type WorkQueueKind = z.infer<typeof workQueueKindSchema>;

export const getWorkQueueInput = z
  .object({
    kind: workQueueKindSchema,
    limit: z.number().int().min(1).max(50).default(10),
  })
  .strict();

// --- Strategies -------------------------------------------------------------

export const listStrategiesInput = z
  .object({
    type: strategyTypeSchema.optional(),
    include_inactive: z.boolean().default(false),
  })
  .strict();

export const getStrategyInput = z
  .object({
    key: strategyKeySchema,
    /** Omit for the active version — which is what a run should normally use. */
    version: z.number().int().min(1).optional(),
  })
  .strict();

export const proposeStrategyVersionInput = z
  .object({
    key: strategyKeySchema,
    playbook_md: markdownSchema,
    change_note: z.string().min(1).max(2000),
  })
  .strict();

// --- Companies --------------------------------------------------------------

const companyInput = z.object({
  name: z.string().min(1).max(200),
  domain: domainSchema,
  industry: z.string().max(120).optional(),
  size_band: z.string().max(40).optional(),
  employee_count: z.number().int().min(0).max(10_000_000).optional(),
  region: z.string().max(120).optional(),
  source_type: sourceTypeSchema.default("cold"),
  /** Required: docs/03 §4 — a company with no stated reason is not a lead. */
  sourcing_rationale_md: markdownSchema.min(
    20,
    "Say why this company qualified — one sentence is not enough to judge later.",
  ),
  score: scoreSchema.optional(),
  score_rationale_md: markdownSchema.optional(),
  hubspot_id: z.string().max(120).optional(),
  warm_trigger: warmTriggerSchema.optional(),
});

export const createCompaniesInput = z
  .object({
    run_id: runIdSchema,
    companies: z.array(companyInput).min(1).max(MAX_COMPANIES_PER_CALL),
  })
  .strict();

export const updateCompanyInput = z
  .object({
    run_id: runIdSchema,
    id: idSchema,
    patch: z
      .object({
        name: z.string().min(1).max(200).optional(),
        industry: z.string().max(120).nullish(),
        size_band: z.string().max(40).nullish(),
        employee_count: z.number().int().min(0).max(10_000_000).nullish(),
        region: z.string().max(120).nullish(),
        score: scoreSchema.optional(),
        score_rationale_md: markdownSchema.optional(),
        stage: companyStageSchema.optional(),
        /** Mandatory when moving to `parked` or `killed`. */
        reason: z.string().min(3).max(2000).optional(),
        hubspot_id: z.string().max(120).nullish(),
        /** Merged key-by-key into existing facts; it never replaces the object. */
        enrichment: enrichmentFactsSchema.optional(),
        research_md: markdownSchema.optional(),
        warm_trigger: warmTriggerSchema.optional(),
      })
      .strict()
      .refine((p) => Object.keys(p).length > 0, "Patch is empty."),
  })
  .strict();

export const getCompanyInput = z
  .object({ id: idSchema })
  .strict();

export const searchCompaniesInput = z
  .object({
    /** Substring match on name or domain. */
    query: z.string().max(200).optional(),
    stage: z.array(companyStageSchema).optional(),
    industry: z.string().max(120).optional(),
    size_band: z.string().max(40).optional(),
    source_type: sourceTypeSchema.optional(),
    sourcing_strategy_key: strategyKeySchema.optional(),
    min_score: scoreSchema.optional(),
    has_contacts: z.boolean().optional(),
    /** Enrichment older than ENRICHMENT_STALE_DAYS, or never enriched. */
    stale_enrichment: z.boolean().optional(),
    limit: z.number().int().min(1).max(100).default(25),
    offset: z.number().int().min(0).default(0),
    sort: z.enum(["score", "recent", "name"]).default("score"),
  })
  .strict();

// --- Contacts ---------------------------------------------------------------

const contactInput = z.object({
  full_name: z.string().min(1).max(160),
  role_title: z.string().max(200).optional(),
  persona_key: z.string().max(60).optional(),
  seniority: z.string().max(60).optional(),
  email: emailSchema.optional(),
  /**
   * Required whenever an address is supplied. docs/04 C1: a pattern-derived
   * address must be declared `guessed`, never passed off as `found`.
   */
  email_status: emailStatusSchema.optional(),
  linkedin_url: z.string().url().max(500).optional(),
  location: z.string().max(120).optional(),
  research_md: markdownSchema.optional(),
});

export const createContactsInput = z
  .object({
    run_id: runIdSchema,
    company_id: idSchema,
    contacts: z
      .array(
        contactInput.refine(
          (c) => !c.email || Boolean(c.email_status),
          "Set email_status when you supply an email — `guessed` is a valid answer.",
        ),
      )
      .min(1)
      .max(MAX_CONTACTS_PER_CALL),
  })
  .strict();

export const updateContactInput = z
  .object({
    run_id: runIdSchema,
    id: idSchema,
    patch: z
      .object({
        full_name: z.string().min(1).max(160).optional(),
        role_title: z.string().max(200).nullish(),
        persona_key: z.string().max(60).nullish(),
        seniority: z.string().max(60).nullish(),
        email: emailSchema.nullish(),
        email_status: emailStatusSchema.optional(),
        linkedin_url: z.string().url().max(500).nullish(),
        location: z.string().max(120).nullish(),
        status: contactStatusSchema.optional(),
        enrichment: enrichmentFactsSchema.optional(),
        research_md: markdownSchema.optional(),
      })
      .strict()
      .refine((p) => Object.keys(p).length > 0, "Patch is empty."),
  })
  .strict();

// --- Per-item results -------------------------------------------------------

/**
 * Bulk writes report per item rather than failing the batch.
 *
 * An agent that sourced 20 companies where 3 are duplicates and 1 is
 * suppressed has done good work; rejecting all 20 would make it retry blind.
 * `duplicate` returns the existing id so the agent can keep working with it.
 */
export const itemResultSchema = z.object({
  index: z.number().int().min(0),
  status: z.enum(["created", "duplicate", "suppressed", "invalid"]),
  id: z.string().uuid().nullable(),
  /** Domain or email, echoed so the agent can match results to its input. */
  ref: z.string().nullable(),
  message: z.string().nullable(),
});
export type ItemResult = z.infer<typeof itemResultSchema>;

// --- Sequences & sending ----------------------------------------------------

/**
 * One email in a sequence.
 *
 * Length limits here are transport sanity, not the quality bar — docs/04 O1
 * caps bodies at 120 words and subjects at six, but that belongs in the
 * playbook where a strategist can change it, not in a schema that would also
 * bind every future outreach strategy.
 */
const sequenceEmailInput = z.object({
  position: z.number().int().min(1).max(20),
  /** Days after the previous email. 0 for the opener. */
  wait_days: z.number().int().min(0).max(90).default(0),
  subject: z.string().min(1).max(300),
  body_md: z.string().min(1).max(8000),
});

export const createSequenceInput = z
  .object({
    run_id: runIdSchema,
    contact_id: idSchema,
    /** A|B|C when the strategy is producing alternatives for comparison. */
    variant_label: z.string().max(8).optional(),
    /**
     * Groups alternative drafts for the same contact. Omit for the first
     * variant and pass the returned value for its siblings, so the reviewer
     * sees them as one decision rather than three separate ones.
     */
    draft_group: idSchema.optional(),
    rationale_md: markdownSchema.min(
      40,
      "State the angle chosen, the research points used, the social proof used, and the tone rationale — the reviewer reads this beside the emails.",
    ),
    emails: z.array(sequenceEmailInput).min(1).max(12),
  })
  .strict()
  .refine(
    (input) =>
      new Set(input.emails.map((e) => e.position)).size === input.emails.length,
    "Email positions must be unique.",
  );

export const getApprovedSequencesInput = z
  .object({
    limit: z.number().int().min(1).max(50).default(10),
  })
  .strict();

export const markSequenceSendingInput = z
  .object({
    run_id: runIdSchema,
    id: idSchema,
    instantly_campaign_id: z.string().min(1).max(200),
    instantly_url: z.string().url().max(500).optional(),
  })
  .strict();

export const markSequenceSentInput = z
  .object({
    run_id: runIdSchema,
    id: idSchema,
  })
  .strict();

export const recordOutcomeInput = z
  .object({
    run_id: runIdSchema,
    sequence_id: idSchema,
    kind: outcomeKindSchema,
    occurred_at: z.string().datetime().optional(),
    details: z.record(z.unknown()).optional(),
  })
  .strict();

export const getStrategyMetricsInput = z
  .object({ key: strategyKeySchema })
  .strict();

// --- Staged imports ---------------------------------------------------------

export const getPendingImportsInput = z
  .object({
    kind: importKindSchema.optional(),
    limit: z.number().int().min(1).max(20).default(5),
  })
  .strict();

export const completeImportInput = z
  .object({
    run_id: runIdSchema,
    import_id: idSchema,
    status: z.enum(["processed", "failed"]),
    /** What you made of the input — including lines you could not resolve. */
    result_md: markdownSchema.min(
      20,
      "Say what you did with the list, and name anything you could not resolve.",
    ),
    stats: z.record(z.number()).optional(),
  })
  .strict();

export const saveReferenceClientsInput = z
  .object({
    run_id: runIdSchema,
    clients: z.array(referenceClientSchema).min(1).max(100),
    /**
     * Replace the whole list rather than merge. Default is merge-by-name, which
     * is what a run processing one pasted batch should do.
     */
    replace: z.boolean().default(false),
  })
  .strict();

// --- Suppression ------------------------------------------------------------

/**
 * Agents may **add** to the suppression list and never remove from it.
 *
 * The same asymmetry as sequence approval: the safe direction is open, the
 * unsafe one does not exist as a verb. An agent that could un-suppress an
 * address could undo an unsubscribe, which is the one thing that must never
 * happen by accident.
 */
export const addSuppressionInput = z
  .object({
    run_id: runIdSchema,
    entries: z
      .array(
        z.object({
          kind: suppressionKindSchema,
          /** An address, or a bare domain to suppress everyone at a company. */
          value: z.string().min(3).max(320),
          reason: z.enum(["hubspot", "manual"]),
          /** HubSpot record id, or wherever you established this. */
          source_ref: z.string().max(200).optional(),
        }),
      )
      .min(1)
      .max(100),
  })
  .strict();

/**
 * Leave a note on a record.
 *
 * A mutating tool, so it carries a `run_id` like every other — a comment
 * without a run is a comment nobody can trace back to the research that
 * prompted it.
 *
 * There is deliberately no counterpart for resolving one. Deciding that a
 * concern has been dealt with is a judgment, and the standing rule is that
 * agents propose and humans dispose (docs/07 §8).
 */
export const addCommentInput = z
  .object({
    run_id: runIdSchema,
    entity_type: z.enum(["company", "contact", "sequence", "run"]),
    entity_id: z.string().uuid(),
    /** Markdown. Rendered with raw HTML disabled. */
    body_md: z.string().trim().min(1).max(8000),
    /** Set to reply to an existing comment. Threads are one level deep. */
    parent_id: z.string().uuid().optional(),
  })
  .strict();
